<template>
  <div v-if="showWizard" class="mascot" :class="isExpanded ? 'expanded-mascot' : 'minimised-mascot'">
    <icon :type="'mascot'" tabindex="-1" />
  </div>
  <div v-if="showWizard" class="popup-panel" :class="panelClass()">
    <div @click="toggleIsExpanded" class="cursor-pointer" :class="isExpanded ? 'minimise-btn' : 'expand-btn'">
      <icon :type="caretIcon" tabindex="-1" />
    </div>
    <div v-if="isExpanded" class="expanded-content mt-4 text-white">
      <h4>Set Up Checklist</h4>
      <h2 class="header-title">Welcome to Sinchii</h2>

      <!-- Expense Drivers -->
      <div class="d-flex align-items-center gap-1 first-category">
        <div class="d-flex align-items-center justify-content-center border border-secondary rounded-circle circle-icon">
          <icon :type="'cash-right'" />
        </div>
        <h4 class="small-text category-title">Expense Drivers</h4>
        <k-button v-if="!this.businessStartYearDetail.hasSavedWageDetails" class="btn btn-secondary btn-sm focus small-text expense-drivers-btn" type="button" @click="setUp('Expense Drivers')" tabindex="-1">
          Set Up
        </k-button>
        <div v-else class="expense-drivers-tick">
          <icon :type="'tick'" />
        </div>
      </div>

      <!-- Income Drivers -->
      <div class="d-flex align-items-center gap-1 category mt-2">
        <div class="d-flex align-items-center justify-content-center border border-secondary rounded-circle circle-icon">
          <icon :type="'cash-left'" />
        </div>
        <h4 class="small-text category-title">Income Drivers</h4>
        <k-button v-if="!this.businessStartYearDetail.hasSavedAverageIncome" :disabled="incomeDriversLocked" class="btn btn-secondary btn-sm focus small-text income-drivers-btn" type="button" @click="setUp('Income Drivers')" tabindex="-1">
          Set Up
        </k-button>
        <div v-else class="income-drivers-tick">
          <icon :type="'tick'" />
        </div>
      </div>

      <!-- Profit / Loss -->
      <div class="d-flex align-items-center gap-1 category mt-2">
        <div class="d-flex align-items-center justify-content-center border border-secondary rounded-circle circle-icon">
          <icon :type="'up-down'" />
        </div>
        <h4 class="small-text category-title">Profit / Loss</h4>
        <k-button v-if="!this.businessStartYearDetail.forecastAdded" :disabled="profitLossLocked" class="btn btn-secondary btn-sm focus small-text profit-loss-btn" type="button" @click="setUp('Profit / Loss')" tabindex="-1">
          Set Up
        </k-button>
        <div v-else class="profit-loss-tick">
          <icon :type="'tick'" />
        </div>
      </div>

      <!-- Cash Summary -->
      <div class="d-flex align-items-center gap-1 category mt-2">
        <div class="d-flex align-items-center justify-content-center border border-secondary rounded-circle circle-icon">
          <icon :type="'cash'" />
        </div>
        <h4 class="small-text category-title">Cash Summary</h4>
        <k-button v-if="!this.businessStartYearDetail.cashSummaryVisited" :disabled="cashSummaryLocked" class="btn btn-secondary btn-sm focus small-text cash-summary-btn" type="button" @click="setUp('Cash Summary')" tabindex="-1">
          Set Up
        </k-button>
        <div v-else class="cash-summary-tick">
          <icon :type="'tick'" />
        </div>
      </div>

      <!-- Start Year -->
      <div class="d-flex align-items-center gap-1 category mt-2">
        <div class="d-flex align-items-center justify-content-center border border-secondary rounded-circle circle-icon">
          <icon :type="'right-arrow'" />
        </div>
        <h4 class="small-text category-title">Start Year</h4>
        <k-button v-if="!this.businessStartYearDetail.startYear" :disabled="startYearLocked" class="btn btn-secondary btn-sm focus small-text start-year-btn" type="button" @click="setUp('Start Year')" tabindex="-1">
          Set Up
        </k-button>
        <div v-else class="start-year-tick">
          <icon :type="'tick'" />
        </div>
      </div>

      <!-- Settings -->
      <div class="d-flex align-items-center gap-1 category mt-2">
        <div class="d-flex align-items-center justify-content-center border border-secondary rounded-circle circle-icon">
          <i class="icon-account"></i>
        </div>
        <h4 class="small-text category-title">View Settings</h4>
        <k-button class="btn btn-primary btn-sm focus small-text view-settings-btn" type="button" @click="view('Settings')" tabindex="-1">
          View
        </k-button>
      </div>

      <!-- Help -->
      <div class="d-flex align-items-center gap-1 category mt-2">
        <div class="d-flex align-items-center justify-content-center border border-secondary rounded-circle circle-icon">
          <i class="icon-help"></i>
        </div>
        <h4 class="small-text category-title">View Help</h4>
        <k-button class="btn btn-primary btn-sm focus small-text view-help-btn" type="button" @click="view('Help')" tabindex="-1">
          View
        </k-button>
      </div>

      <!-- Education -->
      <div class="d-flex align-items-center gap-1 category mt-2">
        <div class="d-flex align-items-center justify-content-center border border-secondary rounded-circle circle-icon">
          <i class="icon-thinkific mt-2"></i>
        </div>
        <h4 class="small-text category-title">View Education</h4>
        <k-button class="btn btn-primary btn-sm focus small-text view-education-btn" type="button" @click="view('Education')" tabindex="-1">
          View
        </k-button>
      </div>

      <!-- Complete -->
      <div v-if="completeButtonVisible" class="d-flex align-items-center gap-1 category mt-2">
        <k-button class="btn btn-secondary btn-md focus small-text complete-wizard-btn" type="button" @click="complete()" tabindex="-1">
          Let's start exploring Sinchii
        </k-button>
      </div>
    </div>
    <div v-else class="minimised-content text-white d-flex align-items-center gap-1">
      <div v-if="completeButtonVisible" class="d-flex align-items-center gap-1 category mt-2">
        <k-button class="btn btn-secondary btn-md focus small-text complete-wizard-min-btn" type="button" @click="complete()" tabindex="-1">
          Let's start exploring Sinchii
        </k-button>
      </div>
      <h4 v-if="!completeButtonVisible" class="small-text ms-5 current-step-title">{{ currentStep }}</h4>
      <k-button v-if="!completeButtonVisible" class="btn btn-secondary btn-sm focus small-text" type="button" @click="setUp(currentStep)" :class="currentStepBtnClass">
        Set Up
      </k-button>
    </div>
  </div>
</template>

<style lang="scss">
@import "../assets/styles/components/_tutorial-wizard.scss";
</style>

<script>
import icon from "../components/Icon.vue";
import ThinkificService from "../services/thinkific-service";
import HelpService from "../services/help-service";
import store from '@/store';
import BusinessStartYearService from "../services/business-start-year-service";
import BusinessService from "../services/business-service";

export default {
  components: {
    icon
  },
  data() {
    return {
      isExpanded: true,
      businessStartYearDetail: {
        businessID: 0,
        startYear: false,
        hasSavedWageDetails: false,
        hasSavedAverageIncome: false,
        forecastAdded: false,
        cashSummaryVisited: false,
        tutorialWizardCompleted: true
      },
    };
  },
  mounted() {
    this.loadBusinessStartYearDetail();

    ThinkificService.getLink()
      .then(data => {
        this.thinkificLink = data.data.toString();
      });

    HelpService.getLink()
      .then(data => {
        this.helpLink = data.data.toString();
      });
  },
  computed: {
    showWizard() {
      return store.getters.isLoggedIn && !store.getters.isAdmin && store.getters.initialSetUpCompleted &&
        this.businessStartYearDetail && !this.businessStartYearDetail.tutorialWizardCompleted &&
        this.$router.currentRoute.value.name != "FinaliseAccount";
    },
    caretIcon() {
      return this.isExpanded ? "caret-down" : "caret-up";
    },
    currentStep() {
      if (this.businessStartYearDetail.hasSavedWageDetails) {
        if (this.businessStartYearDetail.hasSavedAverageIncome) {
          if (this.businessStartYearDetail.forecastAdded) {
            if (this.businessStartYearDetail.cashSummaryVisited) {
              if (this.businessStartYearDetail.startYear) {
                return "Complete Wizard";
              }
              return "Start Year";
            }
            return "Cash Summary";
          }
          return "Profit / Loss";
        }
        return "Income Drivers";
      }
      return "Expense Drivers";
    },
    incomeDriversLocked() {
      return !this.businessStartYearDetail.hasSavedWageDetails;
    },
    profitLossLocked() {
      return !(this.businessStartYearDetail.hasSavedWageDetails && this.businessStartYearDetail.hasSavedAverageIncome);
    },
    cashSummaryLocked() {
      return !this.businessStartYearDetail.forecastAdded;
    },
    startYearLocked() {
      return !this.businessStartYearDetail.cashSummaryVisited;
    },
    completeButtonVisible() {
      return this.businessStartYearDetail.hasSavedWageDetails && this.businessStartYearDetail.hasSavedAverageIncome &&
        this.businessStartYearDetail.forecastAdded && this.businessStartYearDetail.cashSummaryVisited &&
        this.businessStartYearDetail.startYear;
    },
    currentStepBtnClass() {
      if (this.currentStep == "Expense Drivers") {
        return "expense-drivers-btn";
      } else if (this.currentStep == "Income Drivers") {
        return "income-drivers-btn";
      } else if (this.currentStep == "Profit / Loss") {
        return "profit-loss-btn";
      } else if (this.currentStep == "Cash Summary") {
        return "cash-summary-btn";
      } else if (this.currentStep == "Start Year") {
        return "start-year-btn";
      }
    },
    tutorialWizardUpdated() {
      return this.$store.getters.tutorialWizardUpdate;
    }
  },
  methods: {
    panelClass() {
      if (this.isExpanded) {
        return this.completeButtonVisible ? 'complete-panel' : 'expanded-panel'
      } else {
        return 'minimised-panel';
      }
    },
    toggleIsExpanded() {
      this.isExpanded = !this.isExpanded;
    },
    setUp(category) {
      this.$emit("showWizardGuide", category);
      this.$nextTick(() => {
        if (category == "Expense Drivers") {
          this.$router.push("/expenseDrivers");
        } else if (category == "Income Drivers") {
          this.$router.push("/incomeDrivers");
        } else if (category == "Profit / Loss" || category == "Start Year") {
          this.$router.push("/profitLoss");
        } else if (category == "Cash Summary") {
          this.$router.push("/cashSummary");
        }
      });
    },
    view(category) {
      if (category == "Settings") {
        this.$emit("toggleNavSettings");
      } else if (category == "Help") {
        window.open(this.helpLink, "_blank");
      } else if (category == "Education") {
        window.open(this.thinkificLink, "_blank");
      }
    },
    async complete() {
      await BusinessService.setTutorialWizardCompleted(this.businessStartYearDetail.businessID);
      this.$store.dispatch("tutorialWizardUpdate");
    },
    async loadBusinessStartYearDetail() {
      if (store.getters.isLoggedIn && !store.getters.isAdmin && store.getters.initialSetUpCompleted && store.getters.businessID && store.getters.isSubscribed) {
        this.businessStartYearDetail = await BusinessStartYearService.checkBusinessStartYear();
      }
    },
  },
  watch: {
    tutorialWizardUpdated() {
      this.loadBusinessStartYearDetail();
    }
  }
};
</script>

<style scoped>
.text-white ::v-deep(*) {
  color: white;
}
</style>
